import { Box } from "@chakra-ui/react";
import React from "react";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { useEditFormTemplateContext } from "../../context";
import { getQuestionMode } from "../../types/state";
import { QuestionFormEditDrawer } from "./Edit";
import { SchoolItem } from "./types";
import { QuestionFormView } from "./View";

type Props = {
  section: AF.V3.Section;
  question: AF.V3.Question;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  onUpdate: (
    question: AF.V3.Question,
    verification: AF.V3.FormVerification | undefined,
    deletedVerificationIds: string[] | undefined
  ) => Promise<void>;
  onRemove: (question: AF.V3.Question) => Promise<void>;
  onArchive: (question: AF.V3.Question) => Promise<void>;
  onUnarchive: (question: AF.V3.Question) => Promise<void>;
  otherQuestions: readonly AF.V3.Question[];
};
export const QuestionForm: React.FC<Props> = ({
  section,
  question,
  onUpdate,
  onRemove,
  onArchive,
  onUnarchive,
  schools,
  otherQuestions,
}) => {
  const { state, dispatch } = useEditFormTemplateContext();
  const mode = getQuestionMode(state, question.id);

  const setMode = React.useCallback(
    (mode: "Edit" | "View") => {
      dispatch({
        type: "SetQuestionMode",
        questionId: question.id,
        mode,
      });
    },
    [dispatch, question.id]
  );

  const onEditButtonClick = React.useCallback(() => {
    setMode("Edit");
  }, [setMode]);

  const onCancelButtonClick = React.useCallback(() => {
    setMode("View");
  }, [setMode]);

  const onUpdateButtonClick = React.useCallback(
    async (
      updatedQuestion: AF.V3.Question,
      verification: AF.V3.FormVerification | undefined,
      deletedVerificationIds: string[] | undefined
    ) => {
      if (onUpdate) {
        await onUpdate(updatedQuestion, verification, deletedVerificationIds);
      }
      setMode("View");
    },
    [onUpdate, setMode]
  );

  const onRemoveButtonClick = React.useCallback(() => {
    onRemove(question);
  }, [onRemove, question]);

  const onArchiveButtonClick = React.useCallback(() => {
    onArchive(question);
  }, [onArchive, question]);

  const onUnarchiveButtonClick = React.useCallback(() => {
    onUnarchive(question);
  }, [onUnarchive, question]);

  return (
    <Box id={question.id}>
      <QuestionFormEditDrawer
        isOpen={mode === "Edit"}
        sectionType={section.type}
        question={question}
        onClose={onCancelButtonClick}
        onUpdate={onUpdateButtonClick}
        schools={schools}
        otherQuestions={otherQuestions}
      />
      <QuestionFormView
        question={question}
        onEditButtonClick={onEditButtonClick}
        onRemoveButtonClick={onRemoveButtonClick}
        onArchiveButtonClick={onArchiveButtonClick}
        onUnarchiveButtonClick={onUnarchiveButtonClick}
        schools={schools}
      />
    </Box>
  );
};

export { QuestionFormEditDrawer } from "./Edit";
export { QuestionFormView } from "./View";
